import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, Clock, MapPin, User, X, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, okOrThrow } from '../lib/api';
import { useToast } from './TechComponents';

/**
 * AppointmentReminderPopup — Intrusive popup for imminent appointments.
 *
 * Polls notifications every 30s. Snooze/dismiss state lives on the server
 * (per-user `snoozed_by`/`dismissed_by` fields) so all open tabs stay in
 * sync at the next poll. BroadcastChannel propagates the close instantly
 * across tabs of the same browser.
 */

const POLL_INTERVAL = 30000;

// Singleton BroadcastChannel — shared across all renders so messages don't
// race against component re-mounts. Falls back to null on unsupported
// browsers (Safari < 15.4) — the 30s poll still keeps tabs consistent.
const reminderBroadcast = (() => {
  try { return new BroadcastChannel('reminder_sync'); } catch { return null; }
})();

// Durée de silence appliquée par la croix. La croix postait autrefois un
// `dismiss` que ni le feed ni ce composant ne filtrent : le popup revenait au
// poll suivant, 30 s plus tard (ticket #19138). Un silence court est ce que
// l'utilisateur attend d'une fermeture — et il est, lui, respecté.
const CLOSE_SNOOZE_MINUTES = 15;

// « Ce soir » : on ferme à 17 h, 17-18 h c'est la gestion, et à 18 h vient le
// temps des projets. Le rappel revient donc à 18 h le jour même — ni dans la
// journée, ni repoussé au lendemain (William, 30/09).
const EVENING_HOUR = 18;

// Date du « ce soir » en heure locale, ou null s'il est déjà passé (l'option
// disparaît alors du menu plutôt que de taire le rappel dans le passé).
export function eveningSnoozeUntil(now = new Date()) {
  const evening = new Date(now);
  evening.setHours(EVENING_HOUR, 0, 0, 0);
  return evening > now ? evening : null;
}

export default function AppointmentReminderPopup() {
  const navigate = useNavigate();
  const toast = useToast();
  const [popup, setPopup] = useState(null);
  // null | 'snooze' (taire jusqu'à une date) | 'reschedule' (déplacer l'échéance)
  const [picker, setPicker] = useState(null);

  const checkReminders = useCallback(async () => {
    try {
      const res = await apiFetch('/api/notifications?status=pending').then(r => r.json());
      const nowIso = new Date().toISOString();
      const notifs = (res.notifications || [])
        .filter(n => n.type === 'appointment_reminder')
        .filter(n => !n.snoozed_until || n.snoozed_until <= nowIso)   // reporté → pas de popup
        .filter(n => {
          const sev = n.data?.severity || (n.data?.overdue ? 'critical' : (n.data?.offset_minutes <= 15 ? 'critical' : 'warning'));
          return sev === 'critical';
        });

      if (notifs.length > 0 && !popup) {
        setPopup(notifs[0]);
        setPicker(null);  // un rappel neuf s'ouvre fermé, pas sur le sélecteur du précédent
      } else if (popup && !notifs.some(n => n.id === popup.id)) {
        // Notif disparue côté serveur (snooze/dismiss depuis un autre onglet
        // ou device) — fermer le popup local pour rester cohérent.
        setPopup(null);
      }
    } catch {
      // silent
    }
  }, [popup]);

  useEffect(() => {
    checkReminders();
    const interval = setInterval(checkReminders, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [checkReminders]);

  // Listen to other tabs closing/snoozing the same notification.
  useEffect(() => {
    if (!reminderBroadcast) return;
    const onMsg = (ev) => {
      if (ev.data?.type === 'reminder_dismissed' && popup && popup.id === ev.data.id) {
        setPopup(null);
      }
    };
    reminderBroadcast.addEventListener('message', onMsg);
    return () => reminderBroadcast.removeEventListener('message', onMsg);
  }, [popup]);

  const broadcastDismiss = (id) => {
    try { reminderBroadcast?.postMessage({ type: 'reminder_dismissed', id }); } catch {}
  };

  const handleSnooze = async (minutes) => {
    if (!popup) return;
    const id = popup.id;
    setPopup(null);  // optimistic
    setPicker(null);
    broadcastDismiss(id);
    try {
      await apiFetch(`/api/notifications/${id}/snooze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minutes }),
      });
    } catch {
      // silent — at worst the popup will reappear at the next poll
    }
  };

  // La croix = silence court, pas un « c'est réglé » : l'échéance ne bouge pas
  // et le rappel reste visible dans la cloche.
  const handleClose = () => handleSnooze(CLOSE_SNOOZE_MINUTES);

  const handleSnoozeUntil = async (untilIso) => {
    if (!popup) return;
    const id = popup.id;
    setPopup(null); setPicker(null);
    broadcastDismiss(id);
    try {
      await apiFetch(`/api/notifications/${id}/snooze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ until: untilIso }),
      }).then(okOrThrow);
    } catch (err) { toast.error('Report du rappel impossible : ' + err.message); }
  };

  // Reporter la TÂCHE (≠ taire la notification) : on déplace `planned_start_at`.
  // Le backend remet alors le cycle de rappel à zéro, résout les rappels devenus
  // périmés et resynchronise le calendrier — d'où l'absence de snooze ici.
  const handleReschedule = async (untilIso) => {
    if (!popup) return;
    const d = popup.data || {};
    const taskId = String(d.appointment_id || '').replace(/^task-/, '');
    if (!d.ticket_id || !taskId) return;
    const current = popup;
    setPopup(null); setPicker(null);
    try {
      const res = await apiFetch(`/api/tk/tickets/${d.ticket_id}/tasks/${taskId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planned_start_at: untilIso }),
      });
      if (!res.ok) throw new Error('report refusé');
      // Le backend a résolu les rappels devenus périmés : rien à taire ici.
      broadcastDismiss(current.id);
    } catch {
      // Report non enregistré (droits insuffisants, réseau) : on rouvre plutôt
      // que de laisser croire que c'est fait.
      setPopup(current);
    }
  };

  // Target URL for the "Voir le ticket / rendez-vous" link.
  const popupTarget = (() => {
    const d = popup?.data || {};
    // Rappel de tâche : la fiche s'ouvre déroulée jusqu'à la tâche (même règle que
    // la cloche, notification_target_svc._r_rendez_vous).
    const taskId = String(d.appointment_id || '').startsWith('task-') ? d.appointment_id.slice(5) : '';
    if (d.ticket_id && taskId) return `/tickets/${d.ticket_id}?tache=${encodeURIComponent(taskId)}`;
    if (d.ticket_id) return `/tickets/${d.ticket_id}`;
    if (d.appointment_id) return `/assistant/appointments/${d.appointment_id}`;
    return null;
  })();

  // <a href> lets the browser handle cmd/ctrl/shift/middle-click natively
  // (open in new tab). Left-click snoozes 15 min + navigates in same tab.
  const handleOpen = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      // Browser opens in new tab — snooze 15 min too so the popup doesn't
      // reappear immediately in the current tab.
      handleSnooze(15);
      return;
    }
    e.preventDefault();
    handleSnooze(15);
    if (popupTarget) navigate(popupTarget);
  };

  if (!popup) return null;

  const d = popup.data || {};
  const isOverdue = d.overdue;
  const isTask = !!d.ticket_id;
  const entityLabel = isTask ? 'intervention' : 'rendez-vous';

  return createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.4)',
      animation: 'reminder-fade-in 0.3s ease-out',
    }}>
      <div style={{
        width: 420, maxWidth: 'calc(100vw - 32px)',
        background: '#fff', borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden',
        animation: 'reminder-slide-up 0.3s ease-out',
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          background: isOverdue ? '#dc2626' : '#059669',
          padding: '20px 24px',
          display: 'flex', alignItems: 'center', gap: 12,
          color: '#fff',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: isOverdue ? 'reminder-pulse 1.5s infinite' : 'none',
          }}>
            {isOverdue ? <Bell size={24} /> : <CalendarDays size={24} />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.9 }}>
              {isOverdue
                ? (isTask ? 'Intervention en retard' : 'Rendez-vous en retard')
                : (isTask ? 'Intervention imminente' : 'Rendez-vous imminent')}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>
              {popup.message || d.appointment_title || '(sans titre)'}
            </div>
          </div>
          <button onClick={handleClose} title={`Silence ${CLOSE_SNOOZE_MINUTES} minutes`} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
            color: '#fff', cursor: 'pointer', padding: 6, display: 'flex',
          }}>
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px' }}>
          {/* Ticket badge for tasks */}
          {isTask && d.ticket_number && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 6, marginBottom: 14,
              background: isOverdue ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${isOverdue ? '#fecaca' : '#bbf7d0'}`,
              fontSize: 12, fontWeight: 700, color: isOverdue ? '#dc2626' : '#059669',
              fontFamily: "'IBM Plex Mono', monospace",
            }}>
              Ticket #{d.ticket_number}
            </div>
          )}

          {/* Time */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <Clock size={18} style={{ color: isOverdue ? '#dc2626' : '#059669', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: isOverdue ? '#dc2626' : '#111827' }}>
                {d.datetime_formatted || ''}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {isOverdue
                  ? `Cette ${entityLabel} est en retard`
                  : 'Dans quelques minutes'}
              </div>
            </div>
          </div>

          {/* Person / Client */}
          {d.person_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <User size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
              <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>{d.person_name}</span>
            </div>
          )}

          {/* Location */}
          {d.location && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <MapPin size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
              <span style={{ fontSize: 14, color: '#374151' }}>{d.location}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid #f3f4f6',
          display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
          background: '#fafafa', flexWrap: 'wrap',
        }}>
          {/* Deux gestes distincts, volontairement séparés : « Silence » tait le
              rappel sans toucher au planning ; « Reporter » déplace vraiment
              l'échéance de la tâche (et donc l'événement Outlook). */}
          <select
            value=""
            title="Taire ce rappel — l'échéance de la tâche ne change pas"
            onChange={e => {
              if (e.target.value === 'custom') { setPicker('snooze'); return; }
              if (e.target.value === 'evening') {
                const until = eveningSnoozeUntil();
                if (until) handleSnoozeUntil(until.toISOString());
                return;
              }
              if (e.target.value) handleSnooze(parseInt(e.target.value));
            }}
            style={{
              padding: '8px 12px', fontSize: 12, fontWeight: 600,
              border: '1px solid #d1d5db', borderRadius: 8,
              background: '#fff', color: '#374151', cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}
          >
            <option value="" disabled>Silence…</option>
            <option value="5">5 minutes</option>
            <option value="15">15 minutes</option>
            <option value="30">30 minutes</option>
            <option value="60">1 heure</option>
            <option value="240">Cet après-midi</option>
            {eveningSnoozeUntil() && (
              <option value="evening">{`Ce soir (${EVENING_HOUR} h)`}</option>
            )}
            <option value="1440">Demain</option>
            <option value="custom">📅 Jusqu'à une date…</option>
          </select>
          {isTask && (
            <button
              type="button"
              onClick={() => setPicker(p => (p === 'reschedule' ? null : 'reschedule'))}
              title="Déplacer l'échéance de la tâche — le rappel suivra la nouvelle date"
              style={{
                padding: '8px 12px', fontSize: 12, fontWeight: 600,
                border: `1px solid ${picker === 'reschedule' ? '#059669' : '#d1d5db'}`,
                borderRadius: 8, background: picker === 'reschedule' ? '#f0fdf4' : '#fff',
                color: picker === 'reschedule' ? '#059669' : '#374151', cursor: 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}
            >
              📅 Reporter la tâche
            </button>
          )}
          {picker && (
            <input
              type="datetime-local"
              autoFocus
              min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
              onChange={e => {
                if (!e.target.value) return;
                const iso = new Date(e.target.value).toISOString();
                if (picker === 'reschedule') handleReschedule(iso); else handleSnoozeUntil(iso);
              }}
              style={{
                padding: '8px 10px', fontSize: 12, borderRadius: 8,
                border: `1px solid ${picker === 'reschedule' ? '#059669' : '#d1d5db'}`,
              }}
            />
          )}
          <a
            href={popupTarget || '#'}
            onClick={handleOpen}
            style={{
              padding: '8px 20px', fontSize: 13, fontWeight: 600,
              border: 'none', borderRadius: 8,
              background: isOverdue ? '#dc2626' : '#059669', color: '#fff', cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
              textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
            }}
          >
            {isTask ? 'Voir le ticket' : 'Voir le rendez-vous'}
          </a>
        </div>
      </div>

      <style>{`
        @keyframes reminder-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes reminder-slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes reminder-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
      `}</style>
    </div>,
    document.body
  );
}
